import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Spinner, useToast } from '@/components/common';
import { useOnline, useResource } from '@/hooks';
import { currentLanguage, LOCALE_TAG } from '@/i18n';
import { errorMessage } from '@/lib/api-client';
import type { Booking, Clinic } from '@/types/api';
import { addDays, startOfWeek, weekDays } from '@/utils/calendar';
import type { Doctor } from '@/utils/doctors';
import { type BookingInput, bookingsApi } from './api';
import { BookingSheet } from './BookingSheet';
import { type BookingChange, WeekCalendar } from './WeekCalendar';

type Props = {
  clinic: Clinic | null;
  /** The clinic's doctors, for the booking's doctor. */
  doctors: Doctor[];
  /** The doctor picked in the header: the default for new bookings. */
  doctorId: string | null;
  /** The doctor filter (see CalendarFilter): null shows every doctor. */
  filter: string | null;
};

type Editing = { booking: Booking | null; start: Date };

/** The week shown, with the month in words: "26.–30. oktobar 2026.". */
function weekLabel(weekStart: Date, days: Date[]): string {
  return new Intl.DateTimeFormat(LOCALE_TAG[currentLanguage()], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatRange(days[0] ?? weekStart, days.at(-1) ?? weekStart);
}

/** The shared clinic calendar: one week, optionally one doctor's, and the booking sheet. */
export function CalendarPanel({ clinic, doctors, doctorId, filter }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const online = useOnline();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [editing, setEditing] = useState<Editing | null>(null);
  const weekEnd = addDays(weekStart, 7);
  const bookings = useResource(
    () => bookingsApi.list(weekStart, weekEnd, filter),
    `${weekStart.toISOString()}|${filter ?? ''}`
  );
  const { setData, reload } = bookings;

  /** Whether a saved booking belongs in the week and doctor shown. */
  const shownFrom = weekStart.getTime();
  const shows = useCallback(
    (b: Booking) => {
      const at = new Date(b.startsAt).getTime();
      const inWeek = at >= shownFrom && at < shownFrom + 7 * 24 * 60 * 60 * 1000;
      return inWeek && (!filter || b.dentist?.id === filter);
    },
    [shownFrom, filter]
  );

  /** Apply a change at once; on failure put the server's state back. */
  const apply = useCallback(
    async (run: () => Promise<Booking | null>, optimistic?: (list: Booking[]) => Booking[]) => {
      if (optimistic) setData((list) => (list ? optimistic(list) : list));
      try {
        const saved = await run();
        if (saved) {
          setData((list) => {
            const others = (list ?? []).filter((b) => b.id !== saved.id);
            return shows(saved) ? [...others, saved] : others;
          });
        }
      } catch (err) {
        toast.show(errorMessage(err), 'error');
        await reload();
        throw err;
      }
    },
    [setData, reload, toast, shows]
  );

  const save = (input: BookingInput) => {
    const current = editing?.booking;
    return apply(() =>
      current ? bookingsApi.update(current.id, input) : bookingsApi.create(input)
    );
  };

  const change = (booking: Booking, c: BookingChange) =>
    apply(
      () => bookingsApi.update(booking.id, c),
      (list) => list.map((b) => (b.id === booking.id ? { ...b, ...c } : b))
    ).catch(() => undefined);

  const cancel = (booking: Booking) =>
    apply(
      async () => {
        await bookingsApi.cancel(booking.id);
        return null;
      },
      (list) => list.filter((b) => b.id !== booking.id)
    );

  const days = weekDays(weekStart, clinic?.showWeekend ?? false);
  const label = weekLabel(weekStart, days);

  return (
    <section className="home__calendar" aria-label={t('home.bookings')}>
      <div className="cal-toolbar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          aria-label={t('calendar.prevWeek')}
        >
          <IconChevronLeft size={22} />
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => setWeekStart(startOfWeek(new Date()))}
        >
          {t('calendar.thisWeek')}
        </button>
        <span className="cal-toolbar__label">{label}</span>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          aria-label={t('calendar.nextWeek')}
        >
          <IconChevronRight size={22} />
        </button>
      </div>

      {!online && <Banner tone="info">{t('calendar.offline')}</Banner>}
      {bookings.error && online ? (
        <Banner tone="error">{errorMessage(bookings.error)}</Banner>
      ) : null}
      {clinic && (bookings.data || !online) ? (
        <WeekCalendar
          clinic={clinic}
          weekStart={weekStart}
          bookings={bookings.data ?? []}
          readOnly={!online}
          showDoctor={!filter}
          onSlot={(start) => setEditing({ booking: null, start })}
          onOpen={(booking) => setEditing({ booking, start: new Date(booking.startsAt) })}
          onChange={change}
        />
      ) : (
        <Spinner />
      )}

      {editing && (
        <BookingSheet
          key={editing.booking?.id ?? editing.start.toISOString()}
          booking={editing.booking}
          start={editing.start}
          doctors={doctors}
          defaultDoctorId={filter ?? doctorId}
          onClose={() => setEditing(null)}
          onSave={save}
          onCancelBooking={editing.booking ? () => cancel(editing.booking as Booking) : undefined}
        />
      )}
    </section>
  );
}
